
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import MultilevelCategoriesFetcher from "@/components/app/admin/catalogue/products/multilevelCategories/multilevelCategoriesFetchMixin";
import ContractProductsFiltersMixins from "@/mixins/provisioning/contractProductsFiltersMixins";

// Enums
import { ProductTypes } from "@/data/enums/products";
import { productsFilters } from "@/data/filters/products";
import { productsSorters } from "@/data/sorters/products";
import { DataModules } from "@/store/modules/data";

// Types
import { IProvider } from "@/models/providers";
import {
  ICatalogueProviderData,
  ICatalogueProviderMethods
} from "@/models/providers/catalogue";
import { IFilter, ISorter } from "@/models/tables";
import { IProduct } from "@/models/products";

@Component
export default class CatalogueProvider
  extends Mixins(
    RequestPromises,
    MultilevelCategoriesFetcher,
    ContractProductsFiltersMixins
  )
  implements IProvider {
  // Props

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data

  public filters: IFilter[] = [];
  public hasActiveFiltres = false;
  public hasRequestError = false;
  public isLoading = false;
  public isReloading = false;
  public sorters: ISorter[] = [];

  // Computed

  public get itemIsOption() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION);
  }

  public get itemIsAttribute() {
    return this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE);
  }

  public get guardFunctionalities() {
    // Option
    if (this.itemIsOption)
      return ["list_product_options_categories", "list_product_options"];
    // Attribute
    if (this.itemIsAttribute)
      return ["list_product_attributes_categories", "list_product_attributes"];
    // Product
    return ["list_product_categories", "list_products"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get itemRouteName() {
    return this.itemIsOption
      ? "adminCatalogueOption"
      : this.itemIsAttribute
      ? "adminCatalogueAttribute"
      : "adminCatalogueProduct";
  }

  // Methods

  public async loadData() {
    try {
      // Check provisioning first
      await this.getOrgProvisioning(this.isReloading);

      await Promise.all(this.getTableFiltersData());
      this.setFiltersAndSorters();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    await this.loadData();
  }

  public getTableFiltersData() {
    const allPromises = [this.getRootCategories(this.isReloading)];

    if (this.hasProvisioning) {
      allPromises.push(this.getFiltersData(this.isReloading));
    }

    return allPromises;
  }

  public setFiltersAndSorters() {
    this.filters = this.$_.concat(
      this.getProductCategoryFilledFilters(productsFilters(), false),
      this.$_.map(this.createProvisionFilters(false), filter => {
        filter.key = filter.key.replace("product.", ""); // Remove "product." because of different data structure than contract product reused code
        return filter;
      })
    );
    this.sorters = productsSorters;
  }

  public updateHasActiveFilters(value: boolean) {
    this.hasActiveFiltres = value;
  }

  public getProductWithType(productType: ProductTypes) {
    if (productType === ProductTypes.PRODUCT_OPTION)
      return this.$t("_.product_option");

    if (productType === ProductTypes.PRODUCT_ATTRIBUTE)
      return this.$t("_.product_attribute");

    return this.$t("_.product");
  }

  public async openDeleteProductConfirmation(product: IProduct) {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: this.getProductWithType(product.product_type)
              .toString()
              .toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            try {
              await this.deleteProduct(product.id);
              this.$emit("refreshTreeView");
              this.$store.dispatch("toast/show", {
                message: this.$t("_.object_successfully_removed", {
                  object: this.getProductWithType(product.product_type)
                })
              });
              this.$bus.$emit("refreshProductSearchTable");
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            }
            confirm.close();
          }
        }
      }
    });
  }

  public deleteProduct(productId: IProduct["id"]) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/remove`, {
      id: productId
    });
  }

  public async startProductDuplication(product: IProduct) {
    try {
      await this.duplicateProduct(product);
      this.$emit("refreshTreeView");
      this.$store.dispatch("toast/show", {
        message: this.$t("_.object_successfully_duplicated", {
          object: this.getProductWithType(product.product_type)
        })
      });
      this.$bus.$emit("refreshProductSearchTable");
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public duplicateProduct(product) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/duplicate`, {
      id: product.id,
      unitId: this.$_.get(product, "unit_id", null)
    });
  }

  // Life-cycle Methods

  public async created() {
    if (!this.contentIsAvailable) return;
    this.isLoading = true;
    this.loadData();
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $catalogueData: this.providerData(),
        $catalogueMethods: this.providerMethods
      })
    );
  }

  // Provide

  @Provide("catalogueProviderData")
  public providerData(): ICatalogueProviderData {
    return {
      categories: this.categories,
      configurations: this.configurations,
      filters: this.filters,
      hasProvisioning: this.hasProvisioning,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      itemRouteName: this.itemRouteName,
      productTypes: this.productTypes,
      sorters: this.sorters
    };
  }

  @Provide("catalogueProviderMethods")
  public get providerMethods(): ICatalogueProviderMethods {
    return {
      openDeleteProductConfirmation: this.openDeleteProductConfirmation,
      reloadData: this.reloadData,
      startProductDuplication: this.startProductDuplication,
      updateHasActiveFilters: this.updateHasActiveFilters
    };
  }
}
